import React, { useState, useMemo, useRef, useEffect } from 'react';
import Stepper from '@/v2/components/Stepper';
import {
  useCartState,
  useCartDispatch,
} from '../../../components/context/CartContext/CartContext';
import useFetch from 'use-http';
import { useCartData } from '../CartPage/context/CartDataContext';
import { useParams } from 'react-router-dom';
import ComplexBuyerInfo from './Components/ComplexBuyerInfo';
import { ComplexPriceStep } from './Components/ComplexPriceStep';
import ComplexPatientInfoStep from './Steps/ComplexPatientInfoStep';
import { useRegistration } from '../CartPage/context/RegistrationContext';
import { useUserState } from '@/components/context/UserContext';
import personalCodeValidation from '@/components/hooks/personalCodeValidation';
import SuccessStep from '../CartPage/Steps/SuccessStep';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import SuccessStepComplex from './Steps/SuccessStepComplex';
import RegistrationErrorDialog from '../CartPage/Components/RegistrationErrorDialog';
import {
  buildComplexExponeaPayload,
  buildComplexTrackingData,
  clearBookingTrackingData,
  clearExponeaBookingPayload,
  persistBookingTrackingData,
  persistExponeaBookingPayload,
  pushBeginCheckoutTrackingData,
  pushBookingConfirmedEvents,
  pushExponeaBookingItemEvent,
  pushExponeaBookingResultEvent,
  readBookingTrackingData,
  readExponeaBookingPayload,
} from '@/v2/utils/bookingTracking';

const ComplexCartPage = () => {
  const { complex, loading, setLoading, complexPrice } = useCartData();
  const { userInfo } = useRegistration();
  const dispatch = useCartDispatch();
  const buyerFormRef = useRef();
  const { request: registrationRequest, response: registrationResponse } =
    useFetch('/Registrations/ComplexServiceRegistration');
  const GENERIC_ERROR_MSG =
    'Atsiprašome, tačiau jūsų registracija nepavyko dėl nenumatytos klaidos. Bandykite dar kartą.';

  // Error dialog state (for POST errors in natural flow)
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] =
    useState(GENERIC_ERROR_MSG);
  const {
    request: simpleRegistrationRequest,
    response: simpleRegistrationResponse,
  } = useFetch('/Registrations/SimpleComplexRegistration');
  const [stepIndex, setStepIndex] = useState(0);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const { step } = useParams();
  const cartState = useCartState();
  const stepTitles = useMemo(
    () => ['Padalinys', 'Mokėtojo duomenys', 'Apmokėjimas'],
    [],
  );
  const [, , , birthDateFromPersonalCode] = personalCodeValidation();

  const userState = useUserState();
  const history = useHistory();

  useEffect(() => {
    if (complex == null && step !== 'success') {
      if (cartState.registrationId) {
        history.push('/payment/status/cancel');
      } else {
        history.push('/kompleksines-paslaugos');
      }
    }
  }, [complex, history, step]);

  const nextStep = async () => {
    setStepIndex((prev) => {
      const next = Math.min(prev + 1, steps.length - 1);

      if (prev === 0) {
        // Step 0 → just move forward
        return next;
      }

      if (prev === 1) {
        setLoading(true);
        showPaymentOptions(userInfo);
      }

      return next;
    });
  };

  const steps = useMemo(
    () => [
      {
        title: stepTitles[0],
        content: (
          <ComplexPriceStep prices={complex?.prices} goNext={nextStep} />
        ),
      },
      {
        title: stepTitles[1],
        content: (
          <ComplexPatientInfoStep
            ref={buyerFormRef}
            goNext={(formData) => {
              showPaymentOptions(formData);
            }}
          />
        ),
      },
      {
        title: stepTitles[2],
        content: <SuccessStep />,
      },
    ],
    [stepTitles, complex, complexPrice, userInfo],
  );

  useEffect(() => {
    if (step === 'success') {
      pushBookingConfirmedEvents(readBookingTrackingData());
      clearBookingTrackingData();
      pushExponeaBookingResultEvent(readExponeaBookingPayload(), 'Success');
      clearExponeaBookingPayload();

      dispatch({ type: 'RESET_REGISTRATION_ID' });
      if (userState.isLoggedIn) {
        history.push('/userAccount/complex-services');
        return;
      }
      setStepIndex(stepTitles.length);
    }
  }, [step, stepTitles.length, userState.isLoggedIn]);

  useEffect(() => {
    if (!complex || !complexPrice) return;

    pushBeginCheckoutTrackingData(
      buildComplexTrackingData({
        complex,
        complexPrice,
      }),
    );
  }, [complex, complexPrice]);

  const isCompleted = stepIndex === steps.length;
  useEffect(() => {
    const unblock = history.block(() => {
      const nextPath = window.location.pathname;
      if (
        nextPath === '/kompleksines-paslaugos' &&
        stepIndex > 0 &&
        step !== 'success'
      ) {
        setStepIndex((prev) => prev - 1);
        return false;
      }

      return true;
    });

    return () => unblock();
  }, [history, stepIndex]);
  const prevStep = () => setStepIndex((i) => Math.max(i - 1, 0));

  async function showPaymentOptions(formData) {
    setFormSubmitting(true);
    setLoading(true);
    const payload = {
      patientEmail: formData.payerEmail,
      patientName: formData.payerFirstName,
      patientPhoneNumber: formData.payerPhone,
      patientSurname: formData.payerLastName,
      patientIdNumber: userState.isLoggedIn
        ? userState.personalCode
        : formData.payerPersonalCode,
      amount: complexPrice.price,
      complexServiceId: complex.service.serviceId,
      departmentId: complexPrice.departmentId,
      //   price: !!discountCodePrice
      //     ? complexPrice.discountCodePrice
      //     : complexPrice.price,
      price: complexPrice.price,
      giftCouponNumbers: complexPrice.appliedCoupons ?? [],
      discountCode: complexPrice.discountCode,
      priceId: complexPrice.priceId,
      childIdNumber: formData.patientPersonalCode,
      childName: formData.patientFirstName,
      childSurname: formData.patientLastName,
      childEspbiEsi: formData.selectedChildEspbiEsi,
      childBirthDate: formData.patientPersonalCode
        ? birthDateFromPersonalCode(formData.patientPersonalCode)
        : '',
      frontUrls: {
        cancelFrontURL: '/payment/status/cancel',
        acceptFrontUrl: '/kompleksines-paslaugos/cart/success',
        errorFrontUrl: '/complex-payment/error',
      },
    };

    // Optional: remove extra fields if needed
    delete payload.agreement2;
    delete payload.agreement3;

    const buildTracking = (orderId) => ({
      trackingData: buildComplexTrackingData({
        complex,
        complexPrice,
        orderId,
      }),
      exponeaPayload: buildComplexExponeaPayload({
        complex,
        complexPrice,
        formData,
        orderId,
      }),
    });

    // SIMPLE WORK FLOW
    if (complex.simpleWorkflow) {
      const responseData = await simpleRegistrationRequest.post('', payload);
      const orderId =
        responseData?.couponOrderId ||
        responseData?.complexServiceRegistrationId ||
        responseData?.orderId ||
        payload.complexServiceId;
      const { trackingData, exponeaPayload } = buildTracking(orderId);

      setLoading(false);

      if (simpleRegistrationResponse.status > 299) {
        pushExponeaBookingResultEvent(exponeaPayload, 'Failed');
        setErrorDialogMessage(GENERIC_ERROR_MSG);
        setErrorDialogOpen(true);
      } else {
        persistBookingTrackingData(trackingData);
        persistExponeaBookingPayload(exponeaPayload);
        pushExponeaBookingItemEvent(exponeaPayload);
        history.push('/kompleksines-paslaugos/cart/success');
      }
    } else {
      try {
        const responseData = await registrationRequest.post('', payload);
        const orderId = responseData?.couponOrderId;
        const { trackingData, exponeaPayload } = buildTracking(orderId);

        if (registrationResponse.status >= 500) {
          pushExponeaBookingResultEvent(exponeaPayload, 'Failed');
          // setErrorModalVisible(true);
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
        } else if (registrationResponse.status >= 400) {
          const { errors } = responseData;
          pushExponeaBookingResultEvent(exponeaPayload, 'Failed');
          setErrorDialogMessage(GENERIC_ERROR_MSG);
          setErrorDialogOpen(true);
          // handle form errors here if needed
        } else {
          persistBookingTrackingData(trackingData);
          persistExponeaBookingPayload(exponeaPayload);
          pushExponeaBookingItemEvent(exponeaPayload);

          const payseraUrl = responseData.paymentUrl;
          setLoading(false);

          if (!payseraUrl && orderId) {
            history.push('/kompleksines-paslaugos/cart/success');
          } else {
            dispatch({
              type: 'SET_REGISTRATION_ID',
              registrationId: orderId,
              orderType: 'complex',
            });
            window.location.href = payseraUrl;
          }
        }

      } finally {
        setFormSubmitting(false);
      }
    }
  }

  return (
    <div className="w-full max-w-[1440px] mx-auto h-full p-4 font-primary">
      <div className="mb-4">
        <Stepper
          steps={stepTitles}
          currentStep={isCompleted ? steps.length + 1 : stepIndex + 1}
        />
      </div>

      <div className="pb-4 min-h-40">
        {isCompleted ? <SuccessStepComplex /> : steps[stepIndex]?.content}
      </div>

      <RegistrationErrorDialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        message={errorDialogMessage}
      />
    </div>
  );
};

export default ComplexCartPage;
